// src/services/api.js
// CHANGE: 401 interceptor redirects to /nav-login (not /login)
// so an expired navigator session lands on the correct login page.
// Everything else is identical to the original.

import axios from 'axios';
import { handleSessionExpired } from '../utils/sessionExpiry';

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('previu_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-logout on 401/403 — shows a toast then redirects to the correct login page.
// Spring Security returns 401 for expired tokens and 403 for malformed/invalid tokens.
// Both mean the session is gone — handle them identically.
// Doctors and navigators → /nav-login
// Patients → /login
// The toast fires via a CustomEvent because we can't use React hooks in axios
// interceptors. ToastContext listens for 'previu:session-expired' on window.
//
// The shared handler (utils/sessionExpiry.js) clears the stored login, fires the
// toast and redirects once, however many requests fail together. The same
// handler backs a window.fetch guard installed in index.js for screens that
// call fetch() directly instead of this axios instance.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    // A call can opt out of the 403 half with `treat403AsForbidden`: some endpoints refuse a valid login for a business reason (e.g. a
    // report belonging to a patient that is not assigned to this navigator), and that must not end the session. 401 still does.
    const forbiddenIsNotExpiry = status === 403 && err.config?.treat403AsForbidden;
    if ((status === 401 || status === 403) && !forbiddenIsNotExpiry) handleSessionExpired();
    return Promise.reject(err);
  }
);

// ─── Auth ─────────────────────────────────────────────────────────────────
export const authAPI = {
  login: (email, password) =>
    api.post('/auth/login', { email, password }),
  // No register() here. POST /api/auth/register has been removed from the API:
  // it was unauthenticated, took the account's role from the request body, and
  // returned a signed JWT — so anyone could mint themselves an ADMIN token.
  // This wrapper was declared but never called by any component. Staff accounts
  // are created by an admin through navigatorAPI.create().
  // Sends the ID TOKEN (Sign in with Google's `credential`), not the access
  // token the implicit flow used to give us. An ID token is signed and carries
  // an audience claim, so the backend can verify it was minted for Previu —
  // an access token is opaque and proves nothing on its own. The endpoint
  // still accepts { accessToken } for older clients; nothing here sends it.
  googleLogin: (idToken) =>
    api.post('/auth/social/google', { idToken }),
  requestOTP: (phone) =>
    api.post('/auth/social/otp/request', { phone }),
  verifyOTP: (phone, otp) =>
    api.post('/auth/social/otp/verify', { phone, otp }),
};

// ─── Consent (DPDP Act 2023 §6) ───────────────────────────────────────────
// GET  → { coreConsentStatus: 'ACTIVE' | 'WITHDRAWN' | 'NOT_RECORDED',
//          consents: [{ id, purpose, version, consentedAt, revokedAt,
//                       revocationReason, active }] }  (newest first)
// A withdrawn CORE_SERVICE consent makes chat, triage and uploads answer
// 423 { error: 'CONSENT_WITHDRAWN', message } — see isConsentWithdrawn().
export const consentAPI = {
  list:    (patientId) => api.get(`/patients/${patientId}/consents`),
  revoke:  (patientId, consentId, reason) =>
    api.post(`/patients/${patientId}/consents/${consentId}/revoke`, { reason: reason || null }),
  grant:   (patientId, purpose = 'CORE_SERVICE') =>
    api.post(`/patients/${patientId}/consents`, { purpose }),
};

export const isConsentWithdrawn = (err) =>
  err?.response?.status === 423 && err?.response?.data?.error === 'CONSENT_WITHDRAWN';

// The processing gate fails closed: a patient with no consent on file (and no
// legacy exemption) gets 423 { error: 'CONSENT_REQUIRED' } on the same paths.
// Distinct from withdrawn — they never gave it, so the way forward is the
// consent/invitation screen, not Settings > Privacy & consent.
export const isConsentRequired = (err) =>
  err?.response?.status === 423 && err?.response?.data?.error === 'CONSENT_REQUIRED';

// ─── Corporate onboarding (staff) ─────────────────────────────────────────
// ADMIN / HEALTH_NAVIGATOR only. A roster upload creates INVITATIONS, never
// patients — nobody is a patient until they accept the consent themselves.
//   importRoster → { created, skipped, invites: [{ row, firstName, lastName,
//                    employeeRef, claimUrl, expiresAt }], issues: [{ row, message }] }
//                  claimUrl is returned ONCE; only its hash is stored.
//   overview     → { organizationId, organizationName, counts: { PENDING, ACCEPTED,
//                    DECLINED, EXPIRED, REVOKED }, invites: [{ id, status, firstName,
//                    lastName, maskedPhone, employeeRef, invitedAt, expiresAt }] }
export const organizationAPI = {
  list:   () => api.get('/organizations'),
  create: (name) => api.post('/organizations', { name }),
  overview: (id) => api.get(`/organizations/${id}/invites`),
  importRoster: (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/organizations/${id}/invites/import`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  revoke: (id, inviteId) => api.post(`/organizations/${id}/invites/${inviteId}/revoke`),
};

// ─── Corporate onboarding (the invitee) ───────────────────────────────────
// Public: the link token is the credential, so these must NOT go through the
// shared `api` instance — its interceptors would attach whatever session happens
// to be in localStorage and treat a 401/403 as an expired login.
const publicApi = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

//   view   → { status, organisationName, firstName, maskedPhone, consentText,
//              consentVersion, expiresAt }; status other than PENDING means the
//              invite has ended and every other field is null
//   accept → the same payload as an OTP login ({ token, patientId, name, ... })
//   Errors: 404 bad link · 410 ended · 422 wrong code / consent not given ·
//           429 { error: COOLDOWN | LIMIT_REACHED } · 502 { error: DELIVERY_FAILED }
export const inviteAPI = {
  view:    (token) => publicApi.get(`/public/invites/${token}`),
  sendOtp: (token) => publicApi.post(`/public/invites/${token}/otp`),
  accept:  (token, body) => publicApi.post(`/public/invites/${token}/accept`, body),
  decline: (token, otp) => publicApi.post(`/public/invites/${token}/decline`, { otp }),
};

// ─── Patients ─────────────────────────────────────────────────────────────
export const patientAPI = {
  // status (ACTIVE/PENDING/INACTIVE) and joined (week|month) are filtered by
  // the server, so paging and totals describe the filtered set.
  list: (params) => {
    const filters = {
      ...(params?.status && { status: params.status }),
      ...(params?.joined && { joined: params.joined }),
    };
    if (params?.search) {
      return api.get('/patients/search', {
        params: { searchTerm: params.search, page: params.page || 0, size: params.size || 10, ...filters }
      });
    }
    return api.get('/patients', {
      params: { page: params?.page || 0, size: params?.size || 10, ...filters }
    });
  },
  get:    (id)       => api.get(`/patients/${id}`),
  create: (data)     => api.post('/patients', data),
  update: (id, data) => api.put(`/patients/${id}`, data),
  delete: (id)       => api.delete(`/patients/${id}`),
  stats:  ()         => api.get('/patients/stats/total-active'),
  // Oct 2026 — staff (ADMIN/HEALTH_NAVIGATOR) toggles for the patient's
  // WhatsApp and in-app notifications. Both fields are required in the body;
  // responds with the updated preferences. Deliberately its own endpoint
  // rather than update(): PUT /patients/{id} writes no audit row.
  updateNotificationPreferences: (id, body) => api.put(`/patients/${id}/notification-preferences`, body),
  // Profile photo — GET proxies image bytes through the backend rather than
  // handing out a URL (see PatientAvatarController's own javadoc for why),
  // so this is fetched as a blob and turned into an object URL by the
  // caller, not used directly as an <img> src.
  getAvatar:    (id)       => api.get(`/patients/${id}/avatar`, { responseType: 'blob' }),
  uploadAvatar: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/patients/${id}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAvatar: (id) => api.delete(`/patients/${id}/avatar`),
  // 8 Sep 2026 — CSV export, ADMIN/HEALTH_NAVIGATOR only on the backend.
  // Exports every patient matching the current search/status filter (not
  // just the current page) — mirrors list()'s params, minus pagination.
  export: (params) => api.get('/patients/export', {
    params: {
      ...(params?.search && { searchTerm: params.search }),
      ...(params?.status && params.status !== 'ALL' && { status: params.status }),
    },
    responseType: 'blob',
  }),
  // Aug 2026 — bulk import from an .xlsx spreadsheet, ADMIN/HEALTH_NAVIGATOR
  // only (backend-enforced). Response: { created, updated, skipped,
  // errors: [{ row, reason }] }.
  importXlsx: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/patients/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // The patient's transaction trail — uploads, triage runs, payments, lab
  // orders, appointments, critical escalations, the intervention date.
  // Written since V79 and, until now, readable only from a SQL client.
  // Staff-only on the backend (ADMIN / HEALTH_NAVIGATOR / DOCTOR).
  auditLog: (id, params) => api.get(`/patients/${id}/audit-log`, {
    params: { page: params?.page ?? 0, size: params?.size ?? 20 },
  }),
};

// ─── CTC camp screenings ──────────────────────────────────────────────────
// Sep 2026 — camp-screened people, stored in ctc_screening_records rather than
// patients: they have no Previu login, a Navigator manages them entirely, and
// they become a Patient only through convertToPatient. ADMIN/HEALTH_NAVIGATOR
// only on the backend (DELETE is ADMIN-only).
export const ctcScreeningAPI = {
  // params: { status?, page, size } — status is one of SCREENED /
  // FOLLOW_UP_NEEDED / IN_TREATMENT / CONVERTED_TO_PATIENT / CLOSED; omit for all.
  list:   (params)   => api.get('/ctc-screenings', { params }),
  search: (searchTerm, params) => api.get('/ctc-screenings/search', { params: { searchTerm, ...params } }),
  get:    (id)       => api.get(`/ctc-screenings/${id}`),
  create: (data)     => api.post('/ctc-screenings', data),
  update: (id, data) => api.put(`/ctc-screenings/${id}`, data),
  delete: (id)       => api.delete(`/ctc-screenings/${id}`),
  // { aadharNumber } — the unmasked value (null if none on file). Every call is
  // audited server-side, so only call it on an explicit "Show" click, never on
  // page load. list/get/update responses carry aadharNumberMasked only.
  getAadharNumber: (id) => api.get(`/ctc-screenings/${id}/aadhar-number`),
  // { totalScreened, openCases, inTreatment, converted }
  stats:  ()         => api.get('/ctc-screenings/stats'),
  // Response is a full PatientDTO.Response — the same shape GET /patients/{id} returns.
  convertToPatient: (id, data) => api.post(`/ctc-screenings/${id}/convert-to-patient`, data),
  // CREATE-only, unlike patientAPI.importXlsx. Response: { created, skipped,
  // errors: [{ row, reason }] } — no `updated` count.
  importXlsx: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/ctc-screenings/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ─── Addresses ────────────────────────────────────────────────────────────
export const addressAPI = {
  listByPatient: (patientId)              => api.get(`/patients/${patientId}/addresses`),
  create:        (patientId, data)        => api.post(`/patients/${patientId}/addresses`, data),
  update:        (patientId, addressId, data) => api.put(`/patients/${patientId}/addresses/${addressId}`, data),
  delete:        (patientId, addressId)   => api.delete(`/patients/${patientId}/addresses/${addressId}`),
};

// ─── Appointments ──────────────────────────────────────────────────────────
export const appointmentAPI = {
  listByPatient:         (patientId) => api.get(`/appointments/patient/${patientId}`),
  listUpcomingByPatient: (patientId) => api.get(`/appointments/patient/${patientId}/upcoming`),
  list:         (params)     => api.get('/appointments', { params }),
  get:          (id)         => api.get(`/appointments/${id}`),
  create:       (data)       => api.post('/appointments', data),
  updateStatus: (id, status) => api.patch(`/appointments/${id}/status`, { status }),
  cancel:       (id)         => api.delete(`/appointments/${id}`),
  reschedule:   (id, data)   => api.patch(`/appointments/${id}/reschedule`, data),
  availableSlots: (doctorId, date) =>
    api.get('/appointments/available-slots', { params: { doctorId, date } }),
  // Sep 2026 — sibling of availableSlots() above for the Navigator-as-provider
  // path (Book Appointment page's Doctor/Navigator toggle): same endpoint,
  // now accepts navigatorId instead of doctorId. Kept as its own function
  // rather than an options-object rework of availableSlots() so the existing
  // doctorId call sites (PatientBookAppointmentPage.jsx, BookAppointmentPage.js)
  // don't need to change.
  availableSlotsForNavigator: (navigatorId, date) =>
    api.get('/appointments/available-slots', { params: { navigatorId, date } }),
  // 27 Jul 2026: new — navigator-facing wallet-only booking (Deepu, 27 Jul
  // 2026: navigator bookings are wallet-only, no Razorpay fallback). Replaces
  // the previously unpaid `create()` call from BookAppointmentPage.js —
  // that plain `create()` stays here unchanged for any other caller that
  // still needs an unpaid appointment.
  navigatorBook: (data) => api.post('/appointments/navigator/book', data),
  // Aug 2026 — returns { downloadUrl, expiresAt, fileName }, same shape as
  // healthRecordAPI.getDownloadUrl. Only appointments with a real payment
  // record have an invoice; caller should handle a 400 gracefully.
  downloadInvoice: (patientId, appointmentId) =>
    api.get(`/patients/${patientId}/appointments/${appointmentId}/invoice`),
};

// ─── Health Journey — the patient Today screen's timeline (light assistant mode) ──
export const healthJourneyAPI = {
  get: (patientId) => api.get(`/patients/${patientId}/health-journey`),
};

// "Confirm your follow-up" — a dedicated action booking the tentative
// Health Journey milestone directly with the patient's own assigned
// Navigator (not the general doctor-booking flow, see appointmentAPI above).
export const followUpAPI = {
  availableSlots: (patientId, date) =>
    api.get(`/patients/${patientId}/follow-up/available-slots`, { params: { date } }),
  confirm: (patientId, data) => api.post(`/patients/${patientId}/follow-up/confirm`, data),
};

// ─── Doctor Notes — clinical notes authored by doctors, visible to navigators ──
export const doctorNoteAPI = {
  listByPatient: (patientId)       => api.get('/doctor/notes', { params: { patientId } }),
  create:        (patientId, doctorEmail, content) =>
    api.post('/doctor/notes', { patientId, doctorEmail, content }),
};

// Patient-facing counterpart to doctorNoteAPI.create above — used by the
// Ask Previu's add_navigator_note chat tool (via toolRouter.js, which
// calls the backend directly rather than through this axios instance, but
// this wrapper exists for any other caller). The patient has no doctorEmail
// to send, so PatientNavigatorNoteController resolves the assigned Navigator
// server-side instead. Lands in the same doctor_notes feed doctorNoteAPI
// reads, tagged authorName "Previu Assistant" — not the single overwritable
// patients.notes field patientAPI.update({ notes }) writes.
export const navigatorNoteAPI = {
  addFromAssistant: (patientId, content) =>
    api.post(`/patients/${patientId}/navigator-notes`, { content }),
};

// ─── Doctors ───────────────────────────────────────────────────────────────
export const doctorAPI = {
  list:   (params)     => api.get('/doctors', { params }),
  get:    (id)         => api.get(`/doctors/${id}`),
  create: (data)       => api.post('/doctors', data),
  update: (id, data)   => api.put(`/doctors/${id}`, data),
  delete: (id)         => api.delete(`/doctors/${id}`),
};

// ─── Doctor availability ────────────────────────────────────────────────────
// Two families hitting the same backend logic, split because a doctor's own
// JWT identity does NOT map to doctors.id — DoctorService.createDoctor
// auto-provisions a SEPARATE navigators row (role=DOCTOR) for login, so
// AuthContext's user.id is that navigators.id, not doctors.id. The "me"
// endpoints resolve the doctor server-side from their authenticated email —
// the doctor portal never needs to know or send an id at all. The
// {doctorId}-keyed endpoints are staff-only, using the doctors.id
// doctorAPI.list()/get() already return.
export const doctorAvailabilityAPI = {
  // Self-service — doctor portal only.
  getMyWeekly:     ()               => api.get('/doctors/me/availability/weekly'),
  setMyWeekly:     (data)           => api.put('/doctors/me/availability/weekly', data),
  getMyOverrides:  (params)         => api.get('/doctors/me/availability/overrides', { params }),
  setMyOverride:   (date, data)     => api.put(`/doctors/me/availability/overrides/${date}`, data),
  clearMyOverride: (date)           => api.delete(`/doctors/me/availability/overrides/${date}`),
  getMyAuditLog:   (params)         => api.get('/doctors/me/availability/audit-log', { params }),

  // Staff — navigator portal, editing any doctor by their doctors.id.
  getWeekly:       (doctorId)             => api.get(`/doctors/${doctorId}/availability/weekly`),
  setWeekly:       (doctorId, data)       => api.put(`/doctors/${doctorId}/availability/weekly`, data),
  getOverrides:    (doctorId, params)     => api.get(`/doctors/${doctorId}/availability/overrides`, { params }),
  setOverride:     (doctorId, date, data) => api.put(`/doctors/${doctorId}/availability/overrides/${date}`, data),
  clearOverride:   (doctorId, date)       => api.delete(`/doctors/${doctorId}/availability/overrides/${date}`),
  getAuditLog:     (doctorId, params)     => api.get(`/doctors/${doctorId}/availability/audit-log`, { params }),
};

// Navigator's own availability — self-service only, "on his login, can set
// up his schedule / non-availability just like a doctor." No identity-
// duality problem to route around here (see doctorAvailabilityAPI's own
// comment above) — a navigator's JWT resolves straight to their own
// navigators.id, so there's no separate {navigatorId}-keyed staff family
// yet (see NavigatorAvailabilityController's javadoc if that's ever needed).
export const navigatorAvailabilityAPI = {
  getMyWeekly:     ()               => api.get('/navigators/me/availability/weekly'),
  setMyWeekly:     (data)           => api.put('/navigators/me/availability/weekly', data),
  getMyOverrides:  (params)         => api.get('/navigators/me/availability/overrides', { params }),
  setMyOverride:   (date, data)     => api.put(`/navigators/me/availability/overrides/${date}`, data),
  clearMyOverride: (date)           => api.delete(`/navigators/me/availability/overrides/${date}`),
  getMyAuditLog:   (params)         => api.get('/navigators/me/availability/audit-log', { params }),
};

// ─── Health Records ────────────────────────────────────────────────────────
export const recordAPI = {
  listByPatient: (patientId, params) =>
    api.get(`/patients/${patientId}/records`, { params }),
  upload: (patientId, formData) =>
    api.post(`/patients/${patientId}/records`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  delete: (patientId, recordId) =>
    api.delete(`/patients/${patientId}/records/${recordId}`),
};

// ─── Report feature switches ───────────────────────────────────────────────
// Which optional parts of the health report the server has switched on
// (previu.report.clinical-detail-enabled in application.properties).
export const reportConfigAPI = {
  get: () => api.get('/config/report'),
};

// ─── Pre-Call Briefing ─────────────────────────────────────────────────────
export const preCallBriefingAPI = {
  get: (patientId) => api.get(`/patients/${patientId}/pre-call-briefing`),
};

// ─── Effective Panel (correction reports, Phase 2) ─────────────────────────
// The patient's current marker picture assembled across every draw on file —
// fresh (within its validity window), stale (aged out but still shown), and
// never-tested — rather than whichever single triage ran most recently.
export const effectivePanelAPI = {
  get: (patientId) => api.get(`/patients/${patientId}/effective-panel`),
};

// ─── Confounds (V124) ───────────────────────────────────────────────────────
// A log of known confounds per patient (illness, medication, non-fasting
// draw, etc.) that suppress escalation on the risk-scoring path — see
// PatientConfoundController on the backend. One endpoint shape serves all
// three sources (patient self-report, navigator, doctor); the `source` field
// in the payload distinguishes who logged it.
export const confoundAPI = {
  list: (patientId) => api.get(`/patients/${patientId}/confounds`),
  log: (patientId, data) => api.post(`/patients/${patientId}/confounds`, data),
  resolve: (patientId, confoundId, dateResolved) =>
    api.patch(`/patients/${patientId}/confounds/${confoundId}/resolve`,
      dateResolved ? { dateResolved } : {}),
  // Re-scores an already-saved triage in place after a confound is
  // confirmed against it — see TriageReevaluationController on the backend.
  // Staff-only; can only lower or hold the stored risk, never raise it.
  reevaluate: (patientId, triageResultId, confoundId, triggeredByStaffId) =>
    api.post(`/patients/${patientId}/triage-results/${triageResultId}/re-evaluate`,
      { confoundId, triggeredByStaffId }),
};

// ─── Payments ─────────────────────────────────────────────────────────────
export const paymentAPI = {
  sendPaymentLink: (data) => api.post('/payments/send-payment-link', data),
  // 15 Jul 2026: added for the "pay before Thyrocare order creation" flow —
  // gates chat and self-service lab-test ordering on a verified Razorpay
  // payment. createLabTestPaymentOrder resolves the amount server-side
  // (never trust a client-supplied amount); payAndCreateLabTestOrder
  // verifies the Checkout signature before Thyrocare's create_order runs.
  createLabTestPaymentOrder: (data) => api.post('/payments/lab-test/create-order', data),
  payAndCreateLabTestOrder:  (data) => api.post('/labtest/orders/pay-and-create', data),
  // 16 Jul 2026: same pattern, for doctor appointments — default behaviour
  // for patient-initiated bookings is now payment-gated (flat ₹250 today,
  // see AppointmentPricingService for the future per-doctor/visit-type seam).
  createAppointmentPaymentOrder: (data) => api.post('/payments/appointment/create-order', data),
  payAndCreateAppointment:       (data) => api.post('/appointments/pay-and-create', data),
};

// ─── WATI WhatsApp ─────────────────────────────────────────────────────────
export const watiAPI = {
  sendActionPlan: (toPhone, patientName, messageText) =>
    api.post('/whatsapp/send-action-plan', { toPhone, patientName, messageText }),
  sendTemplate: (toPhone, templateName, broadcastName, parameters) =>
    api.post('/whatsapp/send-template', { toPhone, templateName, broadcastName, parameters }),
};

// ─── Guardian Dubai — Subscribers ──────────────────────────────────────────
// 29 Jul 2026 — new. Separate from authAPI/patientAPI: a Subscriber is a
// third principal type (see SubscriberContext.js), not staff, not a patient.
export const subscriberAPI = {
  register: (data) => api.post('/subscriber-auth/register', data),
  login:    (data) => api.post('/subscriber-auth/login', data),
  beneficiaries: (subscriberId) =>
    api.get(`/subscribers/${subscriberId}/beneficiaries`),
  // Amount/currency/gateway are ALWAYS resolved server-side from the
  // subscriber's own stored country — this only ever sends which plan +
  // household size (Individual/Couple, per the confirmed pricing sheet).
  createPlanOrder: (subscriberId, plan, householdSize) =>
    api.post(`/subscribers/${subscriberId}/plan/order`, { plan, householdSize }),
  verifyPlanOrder: (subscriberId, data) =>
    api.post(`/subscribers/${subscriberId}/plan/verify`, data),
  // 30 Jul 2026 — new. Subscriber self-service "add a family member".
  addBeneficiary: (subscriberId, data) =>
    api.post(`/subscribers/${subscriberId}/beneficiaries`, data),
  triageReports: (subscriberId, patientId) =>
    api.get(`/subscribers/${subscriberId}/beneficiaries/${patientId}/triage-reports`),
  documents: (subscriberId, patientId) =>
    api.get(`/subscribers/${subscriberId}/beneficiaries/${patientId}/documents`),
  // 30 Jul 2026 — Feature #4. formData must contain 'file' plus optional
  // recordType/drawDate/labName/notes fields, same shape as the existing
  // patient-facing upload.
  uploadDocument: (subscriberId, patientId, formData) =>
    api.post(`/subscribers/${subscriberId}/beneficiaries/${patientId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // 30 Jul 2026 — Feature #3 ("run triage") completion. Mirrors
  // healthRecordAPI.saveExtractedText/saveTriage exactly — same shape,
  // subscriber-scoped URL. Used by useAutoTriage's apiOverrides param.
  saveExtractedText: (subscriberId, patientId, recordId, extractedText, identityMismatchConfirmed, identityMismatchComment) =>
    api.patch(`/subscribers/${subscriberId}/beneficiaries/${patientId}/documents/${recordId}/extracted-text`,
      { extractedText, identityMismatchConfirmed, identityMismatchComment }),
  saveTriage: (subscriberId, patientId, recordId, triageData) =>
    api.post(`/subscribers/${subscriberId}/beneficiaries/${patientId}/documents/${recordId}/triage`, triageData),
  appointments: (subscriberId, patientId) =>
    api.get(`/subscribers/${subscriberId}/beneficiaries/${patientId}/appointments`),
  // 30 Jul 2026 — Enhancement #4: Navigator/Doctor notes visible to subscriber.
  notes: (subscriberId, patientId) =>
    api.get(`/subscribers/${subscriberId}/beneficiaries/${patientId}/notes`),
  // Aug 2026 — returns { downloadUrl, expiresAt, fileName }. Best-effort:
  // no per-transaction payment record exists for subscriber plan purchases,
  // so the amount shown is today's published price, not necessarily what
  // was actually charged historically.
  downloadInvoice: (subscriberId) =>
    api.get(`/subscribers/${subscriberId}/plan/invoice`),
};

// ─── Navigator/Admin — link or detach a patient's subscriber ───────────────
// 30 Jul 2026 — new. Body: { subscriberId: "<uuid>" } to link,
// { subscriberId: null } to detach.
export const patientSubscriberLinkAPI = {
  updateLink: (patientId, subscriberId) =>
    api.put(`/patients/${patientId}/subscriber-link`, { subscriberId }),
};

// ─── Navigator/Admin — subscriber search (staff-only) ──────────────────────
// 30 Jul 2026 — new. Backs SubscriberSearchPicker.jsx, mirroring
// patientAPI.list({ search }) / PatientSearchPicker.jsx exactly.
export const subscriberSearchAPI = {
  search: (q, size = 15) => api.get('/subscribers/search', { params: { q, size } }),
};

// ─── Domestic patient plans (Enhancement #3, 30 Jul 2026) ──────────────────
// Deliberately separate from subscriberAPI's plan endpoints — a domestic
// patient subscribes for themselves, not through the NRI/beneficiary model.
// One ₹250 BASIC tier until V78 (12 Aug 2026), now RECORD_VAULT / NAVIGATOR /
// FAMILY.
export const patientPlanAPI = {
  // 12 Aug 2026 — returns [{ plan, label, amountMinorUnits, currency, current }].
  // Prices come from the server's own config, so this page never hardcodes a
  // price that could drift from what the patient is actually charged.
  catalog: (patientId) => api.get(`/patients/${patientId}/plan/catalog`),
  createOrder: (patientId, plan) => api.post(`/patients/${patientId}/plan/order`, { plan }),
  verifyOrder: (patientId, data) => api.post(`/patients/${patientId}/plan/verify`, data),
  // Aug 2026 — returns { downloadUrl, expiresAt, fileName }. Best-effort:
  // no per-transaction payment record exists for this flow either, so the
  // amount shown is today's published price for the patient's tier.
  downloadInvoice: (patientId) => api.get(`/patients/${patientId}/plan/invoice`),
};

// ─── Guardian Dubai — Magic-link summary ───────────────────────────────────
// Note: this hits a public, non-JSON HTML endpoint directly (not through the
// `api` axios instance) — the token itself is the credential, no JWT
// involved. Kept here only as a URL builder for consistency with the rest
// of this file's exports.
export const guardianAPI = {
  summaryUrl: (token) => `${API_BASE}/guardian/summary/${token}`,
};

// ─── Stats ────────────────────────────────────────────────────────────────
export const statsAPI = {
  dashboard:    ()     => api.get('/stats/dashboard'),
  appointments: (date) => api.get('/stats/appointments', { params: date ? { date } : {} }),
  patients:     ()     => api.get('/stats/patients'),
};

// ─── Lab Tests ─────────────────────────────────────────────────────────────
export const labTestAPI = {
  catalogList: (params) =>
    api.get('/labtest/catalog/list', {
      params: {
        minPrice: params?.minPrice ?? 0,
        maxPrice: params?.maxPrice ?? 10000,
        gender:   params?.gender   ?? 'MALE',
        page:     params?.page     ?? 1,
        pageSize: params?.pageSize ?? 5,
      },
    }),
  // 15 Jul 2026: added for the patient self-service "Order a Lab Test" page —
  // was showing raw Thyrocare catalog SKUs (AAROGYAM 1.1/1.2/1.3/1.4/"1 WITH
  // UTSH") instead of Previu's own T1/T2 panels. This calls the same
  // Previu-branded pricing endpoint the chat's get_panel_pricing tool uses —
  // strips lab-partner naming, returns Previu panel code/name/price plus the
  // resolved skuId/skuType/skuName needed later for create_order.
  panelPricing: (payload) => api.post('/labtest/panel-pricing', payload),
  // panel-pricing (above) returns only a test COUNT, not the itemised list —
  // deliberately, to keep the Previu-branded name/price without pulling in
  // Thyrocare's raw catalog fields. This is the lookup for the itemised
  // list itself: query is the panel's skuId (panel-pricing's own `skuId`
  // field), matched case-insensitively against the synced catalog cache.
  // Same shape toolRouter.js's get_package_details chat tool already uses.
  getPackageDetails: (query) =>
    api.get(`/labtest/catalog/package/${encodeURIComponent(query)}`),
  slotsSearch:  (payload)          => api.post('/labtest/slots/search', payload),
  createOrder:  (payload)          => api.post('/labtest/orders', payload),
  getOrder:     (orderId, params)  => api.get(`/labtest/orders/${encodeURIComponent(orderId)}`, { params }),
  // 27 Jul 2026: new — navigator-facing wallet-only booking (Deepu, 27 Jul
  // 2026), replacing the old Razorpay-popup + unguarded createOrder() flow
  // in useLabTestBooking.js. Debits the patient's wallet server-side before
  // this is even called — see LabTestController.bookOrderByNavigator() /
  // LabTestServiceImpl... wait, no: this calls the plain wallet-debit
  // endpoint (walletAPI.getNavigatorConfig/navigatorDebit below), NOT this
  // navigatorBookOrder — that one's reserved for a future all-in-one
  // navigator flow (see WALLET_BACKEND_INTEGRATION.md); useLabTestBooking.js
  // deliberately still calls plain createOrder() above after the wallet
  // debit succeeds, to avoid double-booking with Thyrocare. Kept here for
  // completeness/symmetry with appointmentAPI.navigatorBook, not currently
  // called by useLabTestBooking.js.
  navigatorBookOrder: (payload) => api.post('/labtest/orders/navigator/book', payload),
  // 28 Jul 2026: navigator-only order cancellation — Thyrocare cancel +
  // automatic wallet refund. orderId is the THYROCARE partner order id
  // (same one getOrder() above already uses), not Previu's internal UUID.
  // reason is optional — Thyrocare requires SOME cancellation reason text
  // in its own API; if omitted, the backend falls back to a generic default.
  cancelOrder: (orderId, reason) =>
    api.delete(`/labtest/orders/${encodeURIComponent(orderId)}/cancel`, { params: { reason } }),
};

// ─── Lab Test Orders (persisted bookings) ─────────────────────────────────
// GET  /api/lab-test-orders — listing: testCode, partnerOrderId, patient*, plan, noOfTests, price, createdAt, status
// POST /api/lab-test-orders
// PUT  /api/lab-test-orders/{id}
export const labTestOrderAPI = {
  list: (params) =>
    api.get('/lab-test-orders', {
      params: {
        patientId: params?.patientId,
        status:    params?.status,
        search:    params?.search,
        page:      params?.page ?? 0,
        size:      params?.size ?? 20,
      },
    }),
  get: (id) => api.get(`/lab-test-orders/${id}`),
  create: (data) => api.post('/lab-test-orders', data),
  update: (id, data) => api.put(`/lab-test-orders/${id}`, data),
  refundRequest: (data) => api.post('/lab-test-orders/refund-request', data),
  // Manually pulls the Thyrocare report right now instead of waiting for a
  // partner webhook to mark the order report-ready — see
  // LabTestOrderController.pullReport (NAVIGATOR/ADMIN only). orderId accepts
  // the same lookup key as labTestAPI.getOrder/cancelOrder above (the
  // Thyrocare partner order id). Fails with a clear error if Thyrocare
  // doesn't actually have a report yet — this doesn't wait or retry.
  pullReport: (orderId) =>
    api.post(`/lab-test-orders/${encodeURIComponent(orderId)}/pull-report`),
  // Manually retries Thyrocare order creation for a local order that was saved
  // without a partner order — see LabTestOrderService.syncToPartner's Javadoc
  // for how that happens (wallet debited, Thyrocare createOrder 400'd, local
  // order saved anyway so the payment isn't lost). NAVIGATOR/ADMIN only.
  // orderId accepts the same lookup keys as pullReport above (partner order
  // id, test code, or UUID).
  syncToPartner: (orderId) =>
    api.post(`/lab-test-orders/${encodeURIComponent(orderId)}/sync-to-partner`),
  // Aug 2026 — returns { downloadUrl, expiresAt, fileName }. Only orders
  // with a recorded payment have an invoice; caller should handle a 400
  // gracefully (e.g. an order still awaiting payment confirmation).
  downloadInvoice: (patientId, orderId) =>
    api.get(`/patients/${patientId}/lab-tests/${orderId}/invoice`),
};

// ─── Navigators (User Management) ─────────────────────────────────────────
// GET    /api/navigators
// GET    /api/navigators/{id}
// POST   /api/navigators
// PUT    /api/navigators/{id}
// GET    /api/navigators/me
// PATCH  /api/navigators/{id}/toggle-active
// PATCH  /api/navigators/{id}/change-password
export const navigatorAPI = {
  list:           ()         => api.get('/navigators'),
  // The caller's own record. Prefer this over scanning list() to find
  // yourself — see src/utils/currentNavigator.js.
  me:             ()         => api.get('/navigators/me'),
  get:            (id)       => api.get(`/navigators/${id}`),
  create:         (data)     => api.post('/navigators', data),
  update:         (id, data) => api.put(`/navigators/${id}`, data),
  toggleActive:   (id)       => api.patch(`/navigators/${id}/toggle-active`),
  changePassword: (id, data) => api.patch(`/navigators/${id}/change-password`, data),
  // Sep 2026 — new. id + name only (not the admin-only list() above, which
  // carries email/phone) — reachable by any HEALTH_NAVIGATOR so the
  // reassignment picker below has something to populate. Backs
  // NavigatorReassignPanel.jsx.
  assignable:     ()         => api.get('/navigators/assignable'),
};

// ─── Navigator/Admin — reassign a patient's navigator ──────────────────────
// Sep 2026 — new. Any navigator may reassign any patient to any other
// navigator (not scoped to "their own" patients, not admin-only — see
// NavigatorAssignmentService's javadoc in the backend). Body:
// { navigatorId: "<uuid>" }. Every change is written to the patient's
// activity trail as NAVIGATOR_REASSIGNED.
export const patientNavigatorAPI = {
  reassign: (patientId, navigatorId) =>
    api.put(`/patients/${patientId}/navigator`, { navigatorId }),
};

// ─── Health Records (extended) ─────────────────────────────────────────────
// Navigator quality check for scanned reports whose two readings disagree.
// GET  /api/scan-quality/queue
// GET  /api/scan-quality/{recordId}
// POST /api/scan-quality/{recordId}/resolve  { resolutions: [{ discrepancyId, action, value?, note? }] }
//   action: ACCEPT_OCR | ACCEPT_CLAUDE | MANUAL (value + note required) | DISCARD (note required)
//   confirmAgreed: the navigator attests the rows both readers agreed on match the original; the report is only released after this.
export const scanQualityAPI = {
  queue:   () => api.get('/scan-quality/queue'),
  approved: (limit = 50) => api.get('/scan-quality/approved', { params: { limit } }),
  detail:  (recordId) => api.get(`/scan-quality/${recordId}`, { treat403AsForbidden: true }),
  resolve: (recordId, resolutions, confirmAgreed = false, columnDates = null, undatedColumn = null, addRows = null, removeAddedRows = null) =>
    api.post(`/scan-quality/${recordId}/resolve`, {
      resolutions, confirmAgreed, ...(columnDates ? { columnDates } : {}), ...(Number.isInteger(undatedColumn) ? { undatedColumn } : {}),
      ...(addRows ? { addRows } : {}), ...(removeAddedRows ? { removeAddedRows } : {}),
    }),
};

export const healthRecordAPI = {
  list:          (patientId, page, size) => {
    const params = {};
    if (page !== undefined) params.page = page;
    if (size !== undefined) params.size = size;
    return api.get(`/patients/${patientId}/health-records`, { params });
  },
  get:              (patientId, recordId)  => api.get(`/patients/${patientId}/health-records/${recordId}`),
  getDownloadUrl:   (patientId, recordId)  => api.get(`/patients/${patientId}/health-records/${recordId}/download`),
  upload: (patientId, file, { recordType, drawDate, labName, notes, extractedText } = {}) => {
    const form = new FormData();
    form.append('file', file);
    if (recordType)    form.append('recordType', recordType);
    if (drawDate)      form.append('drawDate', drawDate);
    if (labName)       form.append('labName', labName);
    if (notes)         form.append('notes', notes);
    if (extractedText) form.append('extractedText', extractedText);
    return api.post(`/patients/${patientId}/health-records`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // DEF-023 follow-up: identityMismatchConfirmed/identityMismatchComment are
  // optional — omit them for the normal case. If the backend detects the
  // document's own name/age disagrees with the patient's profile and these
  // aren't supplied, it responds 409 IDENTITY_MISMATCH with the compared
  // values (see useAutoTriage.js's handling of this same call).
  // startTriage: only the navigator identity-confirmation screens set it — the server then triages the record itself once it is saved.
  // The live upload pipeline leaves it unset because the browser runs that triage.
  saveExtractedText:    (patientId, recordId, extractedText, identityMismatchConfirmed, identityMismatchComment, startTriage) =>
    api.patch(`/patients/${patientId}/health-records/${recordId}/extracted-text`,
      { extractedText, identityMismatchConfirmed, identityMismatchComment, ...(startTriage ? { startTriage: true } : {}) }),
  update:               (patientId, recordId, data) =>
    api.patch(`/patients/${patientId}/health-records/${recordId}`, data),
  saveTriage:           (patientId, recordId, triageData) =>
    api.post(`/patients/${patientId}/health-records/${recordId}/triage`, triageData),
  delete:               (patientId, recordId) =>
    api.delete(`/patients/${patientId}/health-records/${recordId}`),
  getTriageResult:      (patientId, recordId) =>
    api.get(`/patients/${patientId}/health-records/${recordId}/triage/latest`),
  getTriageHistory:     (patientId) =>
    api.get(`/patients/${patientId}/triage-history`),
  updateActionPlanChecks: (patientId, recordId, actionPlanCheckedJson) =>
    api.patch(
      `/patients/${patientId}/health-records/${recordId}/triage/action-plan-checks`,
      { actionPlanCheckedJson }
    ),
  // V108 — the only way out of processingStatus=FAILED. Backend-side
  // (HealthRecordAutoTriageService.retryTriage): one attempt, and it never
  // touches the scheduler's own attempt-count cap, so it stays usable
  // indefinitely. NAVIGATOR/ADMIN only.
  // "Re-run triage" on a record that already has a report: the server marks it as running and runs the triage itself, so it carries on if the
  // tab is closed; progress is read back from the record's processingStatus (EXTRACTING / READING_SCAN while it runs).
  rerunTriage:          (patientId, recordId) =>
    api.post(`/patients/${patientId}/health-records/${recordId}/rerun-triage`),
  retryTriage:          (patientId, recordId) =>
    api.post(`/patients/${patientId}/health-records/${recordId}/retry-triage`),
};

export const walletAPI = {
  getBalance: (patientId) =>
    api.get(`/wallet/${patientId}/balance`),

  getTransactions: (patientId, page = 0, size = 20) =>
    api.get(`/wallet/${patientId}/transactions`, { params: { page, size } }),

  createTopupOrder: (patientId, amountMinorUnits) =>
    api.post(`/wallet/${patientId}/topup/create-order`, { amountMinorUnits }),

  verifyTopup: (patientId, { razorpayOrderId, razorpayPaymentId, razorpaySignature, amountMinorUnits }) =>
    api.post(`/wallet/${patientId}/topup/verify`, {
      razorpayOrderId, razorpayPaymentId, razorpaySignature, amountMinorUnits,
    }),

  // 27 Jul 2026: new — navigator-initiated bookings (lab tests,
  // appointments). getNavigatorConfig tells the frontend whether Razorpay
  // is enabled for navigators at all (see NavigatorPaymentConfig.java —
  // defaults false); navigatorDebit is the generic wallet-only debit used
  // by useLabTestBooking.js (BookAppointmentPage.js uses
  // appointmentAPI.navigatorBook instead, which debits+creates atomically
  // server-side in one call).
  getNavigatorConfig: () => api.get('/wallet/navigator-config'),

  navigatorDebit: (patientId, { amountMinorUnits, referenceType, description }) =>
    api.post(`/wallet/${patientId}/navigator-debit`, { amountMinorUnits, referenceType, description }),
};

export default api;
